<script>
export default {
  name: "FlowBanner",
  props: {
    pageState: {
      type: String,
      default: "IDLE"
    },
    packingMode: {
      type: String,
      default: "NORMAL"
    },
    isExpressMode: {
      type: Boolean,
      default: false
    },
    isExpressAvailable: {
      type: Boolean,
      default: false
    },
    messageType: {
      type: String,
      default: "info"
    },
    nextActionText: {
      type: String,
      default: ""
    },
    progressText: {
      type: String,
      default: ""
    },
    selectedOrder: {
      type: Object,
      default: null
    },
    selectedPartnerLabel: {
      type: String,
      default: ""
    },
    waybillPrintRequested: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    stateColor() {
      if (this.messageType === "negative") return "negative"
      if (this.messageType === "positive") return "positive"
      if (this.isExpressMode) return "orange-8"
      return "primary"
    },
    modeLabel() {
      return this.isExpressMode ? "익스프레스 모드" : "일반 모드"
    },
    modeColor() {
      return this.isExpressMode ? "orange-8" : "primary"
    },
    modeSummary() {
      if (this.isExpressMode) return "상품 한 행, 수량 1개인 주문만 자동 출력·포장완료합니다. 나머지 주문은 일반 포장으로 진행하세요."
      return "상품 바코드, HBL 시작, 상품 확인, HBL 완료 순서로 진행합니다."
    },
    steps() {
      if (this.isExpressMode) {
        return ["상품 바코드", "자동 출력", "자동 완료"]
      }
      return ["상품 바코드", "HBL 시작", "상품 확인", "HBL 완료"]
    }
  }
}
</script>

<template>
  <div class="comp-box flow-banner" :class="{ 'flow-banner--express': isExpressMode }">
    <div class="flow-mode-row">
      <q-badge
        :color="modeColor"
        text-color="white"
        class="flow-mode-badge"
      >
        {{ modeLabel }}
      </q-badge>
      <span class="flow-mode-summary">{{ modeSummary }}</span>
      <q-space />
      <q-badge
        v-if="isExpressMode"
        color="red-8"
        text-color="white"
      >
        스캔 즉시 완료
      </q-badge>
      <q-badge
        v-else
        color="blue-grey-7"
        text-color="white"
      >
        HBL 확인 필요
      </q-badge>
    </div>
    <div class="flow-step-row">
      <div
        v-for="(step, index) in steps"
        :key="step"
        class="flow-step"
        :class="{ 'flow-step--express': isExpressMode }"
      >
        <span class="flow-step-index">{{ index + 1 }}</span>
        <span>{{ step }}</span>
      </div>
    </div>
    <div class="flow-main">
      <q-badge
        :color="stateColor"
        class="q-mr-sm"
      >
        {{ pageState }}
      </q-badge>
      <span class="flow-next">다음 행동: {{ nextActionText }}</span>
      <q-space />
      <span class="flow-progress" v-if="selectedOrder">{{ progressText }}</span>
    </div>
    <div class="flow-action-row">
      <q-badge
        :color="selectedPartnerLabel ? 'indigo-7' : 'grey-6'"
        text-color="white"
      >
        파트너: {{ selectedPartnerLabel || "선택 필요" }}
      </q-badge>
      <q-badge
        :color="waybillPrintRequested ? 'positive' : 'grey-6'"
        text-color="white"
      >
        운송장 출력 상태: {{ waybillPrintRequested ? "요청됨" : "미요청" }}
      </q-badge>
      <q-badge
        v-if="packingMode === 'EXPRESS' && !isExpressAvailable"
        color="grey-7"
        text-color="white"
      >
        익스프레스 비활성
      </q-badge>
      <div v-if="pageState === 'COMPLETED'" class="text-caption text-primary">
        포장 완료: 아래 안내 모달에서 다음 액션을 선택하세요.
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.flow-banner {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-left: 5px solid #1976d2;
}

.flow-banner--express {
  border-left-color: #ef6c00;
  background: #fff8ed;
}

.flow-mode-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.flow-mode-badge {
  min-height: 28px;
  font-weight: 700;
}

.flow-mode-summary {
  font-size: 14px;
  font-weight: 700;
  color: #223044;
}

.flow-step-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.flow-step {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 4px 9px;
  border: 1px solid #c9d8e8;
  border-radius: 6px;
  background: #f8fbff;
  color: #27384f;
  font-size: 12px;
  font-weight: 700;
}

.flow-step--express {
  border-color: #f4c281;
  background: #fffaf0;
  color: #633b0d;
}

.flow-step-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #263a56;
  color: #fff;
  font-size: 11px;
}

.flow-main {
  display: flex;
  align-items: center;
  gap: 8px;
}

.flow-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.flow-next {
  font-size: 13px;
  color: #374151;
}

.flow-progress {
  font-size: 12px;
  color: #4b5563;
}

@media (max-width: 599px) {
  .flow-action-row {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .flow-mode-row {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
